import { HttpBackend, HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchAll, switchMap, take } from 'rxjs/operators';
import { VrcAuthService } from '../services/vrc-auth.service';
import { VrcBrowserStorageService } from '../services/vrc-browser-storage.service';
import { VrcConfigService } from '../services/vrc-config.service';
import { VrcDataService } from '../services/vrc-data.service';
import { VrcStorageService } from '../services/vrc-storage.service';


@Injectable()
export class ValAuthInterceptor implements HttpInterceptor {
  private _bst = inject(VrcBrowserStorageService);
  private _r = inject(Router);
  private _auth = inject(VrcAuthService);
  private _config = inject(VrcConfigService);
  private _st = inject(VrcStorageService);
  private _srvc = inject(VrcDataService);

  private bypassHttpClient: HttpClient;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<unknown> = new BehaviorSubject<unknown>(null);
  constructor() {
    const httpBackend = inject(HttpBackend);

    this.bypassHttpClient = new HttpClient(httpBackend);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    var token = this._bst.token;
    var isUnAuthorizedUrl = req.url.includes(this._config.getTokenUrl) || req.url.includes(this._config.getUserTokenUrl);
    if (token || isUnAuthorizedUrl) {
      return this.executeBypassRequest(req, true);
    }
    return next.handle(req);
  }
  private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    if (!(request.url.includes(this._config.getTokenUrl) || request.url.includes(this._config.getUserTokenUrl))) {
      // If token exists add it to header
      if (this._bst.token) {
        return request.clone({
          headers: request.headers
            .set("Authorization", "Token " +
              (request.url.includes(this._config.refreshTokenUrl) ? this._bst.refreshToken : this._bst.token))
            .set("content-type", "application/json")
        });
      }
    }
    return request;
  }

  private executeBypassRequest(req: HttpRequest<any>, handleRefreshToken: boolean = false): Observable<HttpEvent<any>> {
    let internalRequest = this.addAuthHeader(req);
    return this.bypassHttpClient.request(internalRequest).pipe(
      switchMap(event => {
        if (event instanceof HttpResponse) {
          return of(event.clone());
        }
        return of(event);
      }),
      catchError((err: HttpErrorResponse) => {
        if (handleRefreshToken) {
          return this.handleResponseError(err, req);
        } else {
          if (err.status === 401) {
            const url = this._r.url.includes('login') ? '' : this._r.url;
            this._auth.logout(url);
          }
          return throwError(() => err);
        }
      }));
  }

  private handleResponseError(err: HttpErrorResponse, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const er = { ...err };

    if (er.status === 401) {
      if (req?.url === this._config.refreshTokenUrl) {
        const url = this._r.url.includes('login') ? '' : this._r.url;
        this._auth.logout(url);
        return throwError(() => er);
      } else if (req?.url === this._config.getTokenUrl) {
        const url = this._r.url.includes('login') ? '' : this._r.url;
        this._auth.logout(url);
        return throwError(() => er);
      } else {
        // Nice authorization has priority
        if (this._st.sessionId) {
          if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this._srvc.getToken({ sessionId: this._st.sessionId, agentId: (this._bst.agentId ?? 0), contactId: this._st.contactId() ?? 0 }).pipe(
              map((newToken) => {
                this.isRefreshing = false;
                this._bst.token = newToken.token;
                this._bst.refreshToken = newToken.refreshToken;
                this.refreshTokenSubject.next(newToken.token);
                return this.executeBypassRequest(req);
              }),
              switchAll(),
              catchError((error) => {
                const url = this._r.url.includes('login') ? '' : this._r.url;
                this._auth.logout(url);
                return throwError(() => error);
              }),
              finalize(() => {
                this.isRefreshing = false;
              })
            );
          } else {
            return this.refreshTokenSubject.pipe(
              filter((token) => token !== null),
              take(1),
              map(() => {
                return this.executeBypassRequest(req);
              }),
              switchAll()
            );
          }
        } else {
          if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this._srvc.refreshToken().pipe(
              map((newToken) => {
                this.isRefreshing = false;
                this._bst.token = newToken.token;
                this._bst.refreshToken = newToken.refreshToken;
                this.refreshTokenSubject.next(newToken.token);
                return this.executeBypassRequest(req);
              }),
              switchAll(),
              catchError((error) => {
                const url = this._r.url.includes('login') ? '' : this._r.url;
                this._auth.logout(url);
                return throwError(() => error);
              }),
              finalize(() => {
                this.isRefreshing = false;
              })
            );
          } else {
            // While refreshing, wait for the new token and then retry the request
            return this.refreshTokenSubject.pipe(
              filter((token) => token !== null),
              take(1),
              map(() => {
                return this.executeBypassRequest(req);
              }),
              switchAll()
            );
          }
        }
      }
    }
    return throwError(() => er);
  }
}

